import type { PathTileAtlasId } from '../pathArtStyles';
import { getPathTileArtStyle } from '../pathArtStyles';
import { composePathTileLayers, createCanvas, seededFloat } from './pathTileComposer';

interface Point {
  readonly x: number;
  readonly y: number;
}

export type TrianglePathBrushSpec =
  | { readonly kind: 'mask'; readonly mask: number }
  | { readonly kind: 'variant'; readonly variant: 'two-arm-trapeze' | 'three-arm-trapeze' };

export type TrianglePathFillCorner = 'left' | 'right' | 'apex';

const TRIANGLE_CANONICAL_BRUSH_LABELS: Record<number, string> = {
  0: 'Isolated',
  2: 'Upper Right Arm',
  4: 'Bottom Arm',
  6: 'Right Turn',
  8: 'Upper Left Arm',
  10: 'Bridge',
  12: 'Left Turn',
  14: 'Three-Way Junction',
};

const BRUSH_COUNT = 16;
const RUNTIME_TILE_COUNT = 32;
const previewAtlasCache = new Map<string, HTMLCanvasElement>();
const runtimeAtlasCache = new Map<string, HTMLCanvasElement>();
const tileCache = new Map<string, HTMLCanvasElement>();

let TRIANGLE_EDGE_INSET_RATIO = 0;
let TRIANGLE_SIDE_CONNECTOR_INSET_RATIO = -0.1;
let TRIANGLE_APEX_CONNECTOR_INSET_RATIO = -0.1;
let TRIANGLE_OUTER_WIDTH_RATIO = 0.34;
let TRIANGLE_INNER_WIDTH_SHRINK_RATIO = 0.11;
let TRIANGLE_GLOBAL_SCALE_RATIO = 1.0;

function pointsUp(x: number, y: number): boolean {
  return ((x + y) & 1) === 0;
}

function normalizeTriangleMask(mask: number, up: boolean): number {
  return up ? (mask & (2 | 4 | 8)) : (mask & (1 | 2 | 8));
}

function scalePointAroundCenter(point: Point, center: Point, scale: number): Point {
  return {
    x: center.x + ((point.x - center.x) * scale),
    y: center.y + ((point.y - center.y) * scale),
  };
}

function triangleVertices(size: number, up: boolean): readonly [Point, Point, Point] {
  const topY = size * TRIANGLE_EDGE_INSET_RATIO;
  const bottomY = size * (1 - TRIANGLE_EDGE_INSET_RATIO);
  const leftX = size * TRIANGLE_EDGE_INSET_RATIO;
  const rightX = size * (1 - TRIANGLE_EDGE_INSET_RATIO);
  const centerX = size * 0.5;
  const baseVertices = up
    ? [
      { x: centerX, y: topY },
      { x: rightX, y: bottomY },
      { x: leftX, y: bottomY },
    ]
    : [
      { x: leftX, y: topY },
      { x: rightX, y: topY },
      { x: centerX, y: bottomY },
    ];
  const center = { x: size * 0.5, y: size * 0.5 };
  return baseVertices.map((point) => scalePointAroundCenter(point, center, TRIANGLE_GLOBAL_SCALE_RATIO)) as readonly [Point, Point, Point];
}

function centroidOf(vertices: readonly [Point, Point, Point]): Point {
  return {
    x: (vertices[0].x + vertices[1].x + vertices[2].x) / 3,
    y: (vertices[0].y + vertices[1].y + vertices[2].y) / 3,
  };
}

function midpoint(a: Point, b: Point): Point {
  return {
    x: (a.x + b.x) * 0.5,
    y: (a.y + b.y) * 0.5,
  };
}

function insetToward(point: Point, center: Point, factor: number): Point {
  return {
    x: point.x + ((center.x - point.x) * factor),
    y: point.y + ((center.y - point.y) * factor),
  };
}

function buildConnectorPoints(size: number, up: boolean): Record<'north' | 'east' | 'south' | 'west', Point> {
  const vertices = triangleVertices(size, up);
  const center = centroidOf(vertices);
  const northPoint = up ? vertices[0] : midpoint(vertices[0], vertices[1]);
  const eastPoint = up ? midpoint(vertices[0], vertices[1]) : midpoint(vertices[1], vertices[2]);
  const southPoint = up ? midpoint(vertices[1], vertices[2]) : vertices[2];
  const westPoint = up ? midpoint(vertices[2], vertices[0]) : midpoint(vertices[2], vertices[0]);
  return {
    north: insetToward(northPoint, center, TRIANGLE_APEX_CONNECTOR_INSET_RATIO),
    east: insetToward(eastPoint, center, TRIANGLE_SIDE_CONNECTOR_INSET_RATIO),
    south: insetToward(southPoint, center, TRIANGLE_APEX_CONNECTOR_INSET_RATIO),
    west: insetToward(westPoint, center, TRIANGLE_SIDE_CONNECTOR_INSET_RATIO),
  };
}

function clipToTriangle(context: CanvasRenderingContext2D, size: number, up: boolean): void {
  const vertices = triangleVertices(size, up);
  context.beginPath();
  context.moveTo(vertices[0].x, vertices[0].y);
  context.lineTo(vertices[1].x, vertices[1].y);
  context.lineTo(vertices[2].x, vertices[2].y);
  context.closePath();
  context.clip();
}

function hasBit(mask: number, bit: number): boolean {
  return (mask & bit) !== 0;
}

function drawCornerFill(
  context: CanvasRenderingContext2D,
  size: number,
  mask: number,
  up: boolean,
  color: string,
  corner: TrianglePathFillCorner,
): void {
  const vertices = triangleVertices(size, up);
  const center = centroidOf(vertices);
  const connectors = buildConnectorPoints(size, up);
  const requirements = up
    ? {
      right: { firstBit: 2, firstPoint: connectors.east, secondBit: 4, secondPoint: connectors.south, vertex: vertices[1] },
      left: { firstBit: 4, firstPoint: connectors.south, secondBit: 8, secondPoint: connectors.west, vertex: vertices[2] },
      apex: { firstBit: 8, firstPoint: connectors.west, secondBit: 2, secondPoint: connectors.east, vertex: vertices[0] },
    }
    : {
      right: { firstBit: 1, firstPoint: connectors.north, secondBit: 2, secondPoint: connectors.east, vertex: vertices[1] },
      left: { firstBit: 8, firstPoint: connectors.west, secondBit: 1, secondPoint: connectors.north, vertex: vertices[0] },
      apex: { firstBit: 2, firstPoint: connectors.east, secondBit: 8, secondPoint: connectors.west, vertex: vertices[2] },
    };
  const requirement = requirements[corner];
  if (!hasBit(mask, requirement.firstBit) || !hasBit(mask, requirement.secondBit)) {
    return;
  }
  context.fillStyle = color;
  context.beginPath();
  context.moveTo(center.x, center.y);
  context.lineTo(requirement.firstPoint.x, requirement.firstPoint.y);
  context.lineTo(requirement.vertex.x, requirement.vertex.y);
  context.lineTo(requirement.secondPoint.x, requirement.secondPoint.y);
  context.closePath();
  context.fill();
}

function drawPathLayer(
  context: CanvasRenderingContext2D,
  size: number,
  mask: number,
  up: boolean,
  color: string,
  width: number,
): void {
  const center = centroidOf(triangleVertices(size, up));
  const connectors = buildConnectorPoints(size, up);

  context.strokeStyle = color;
  context.fillStyle = color;
  context.lineWidth = width;
  context.lineCap = 'round';
  context.lineJoin = 'round';

  if (mask === 0) {
    context.beginPath();
    context.arc(center.x, center.y, width * 0.52, 0, Math.PI * 2);
    context.fill();
    return;
  }

  const directions = [
    ['north', 1],
    ['east', 2],
    ['south', 4],
    ['west', 8],
  ] as const;

  context.beginPath();
  for (const [direction, bit] of directions) {
    if (!hasBit(mask, bit)) {
      continue;
    }
    const target = connectors[direction];
    context.moveTo(center.x, center.y);
    context.lineTo(target.x, target.y);
  }
  context.stroke();

  context.beginPath();
  context.arc(center.x, center.y, width * 0.5, 0, Math.PI * 2);
  context.fill();
}

function drawFlowerDecoration(
  context: CanvasRenderingContext2D,
  size: number,
  seed: string,
  up: boolean,
  count: number,
  bloomColors: readonly string[],
  centerColor: string,
  stemColor: string,
): void {
  for (let index = 0; index < count; index += 1) {
    const x = size * (0.22 + (seededFloat(`${seed}:x:${index}`) * 0.56));
    const y = size * (0.22 + (seededFloat(`${seed}:y:${index}`) * 0.5));
    const scale = 0.55 + (seededFloat(`${seed}:s:${index}`) * 0.45);
    const petalRadius = Math.max(1.1, size * 0.024 * scale);
    const bloomColor = bloomColors[index % bloomColors.length] ?? bloomColors[0] ?? '#fff';
    context.strokeStyle = stemColor;
    context.lineWidth = Math.max(1, size * 0.009 * scale);
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(x, y + petalRadius * 2.5);
    context.lineTo(x, y + petalRadius * 0.9);
    context.stroke();
    context.fillStyle = bloomColor;
    for (let petal = 0; petal < 4; petal += 1) {
      const angle = (petal * Math.PI) / 2;
      context.beginPath();
      context.arc(x + Math.cos(angle) * petalRadius, y + Math.sin(angle) * petalRadius, petalRadius * 0.82, 0, Math.PI * 2);
      context.fill();
    }
    context.fillStyle = centerColor;
    context.beginPath();
    context.arc(x, y, petalRadius * 0.5, 0, Math.PI * 2);
    context.fill();
  }
}

export function resolveTrianglePathBrushMask(tileIndex: number): number | null {
  return tileIndex >= 0 && tileIndex < BRUSH_COUNT ? tileIndex : null;
}

export function resolveTrianglePathBrush(tileIndex: number): TrianglePathBrushSpec | null {
  if (tileIndex < 0 || tileIndex >= BRUSH_COUNT) {
    return null;
  }
  if (tileIndex === 8) {
    return { kind: 'variant', variant: 'two-arm-trapeze' };
  }
  if (tileIndex === 12) {
    return { kind: 'variant', variant: 'three-arm-trapeze' };
  }
  return { kind: 'mask', mask: tileIndex };
}

export function resolveTriangleVariantMask(
  variant: 'two-arm-trapeze' | 'three-arm-trapeze',
  up: boolean,
): number {
  if (variant === 'two-arm-trapeze') {
    return up ? (2 | 4) : (1 | 2);
  }
  return up ? (2 | 4 | 8) : (1 | 2 | 8);
}

export function getTrianglePathBrushLabel(tileIndex: number): string {
  const brush = resolveTrianglePathBrush(tileIndex);
  if (!brush) {
    return 'Unknown Triangle Path Brush';
  }
  if (brush.kind === 'variant') {
    return brush.variant === 'two-arm-trapeze'
      ? 'Right Turn Trapeze'
      : 'Three-Way Trapeze';
  }
  const normalizedMask = normalizeTriangleMask(brush.mask, true);
  const canonicalLabel = TRIANGLE_CANONICAL_BRUSH_LABELS[normalizedMask] ?? `Mask ${normalizedMask}`;
  return brush.mask === normalizedMask ? canonicalLabel : `${canonicalLabel} Mirror`;
}

export function trianglePathAtlasTileIndex(mask: number, x: number, y: number): number {
  const up = pointsUp(x, y);
  return normalizeTriangleMask(mask, up) + (up ? 0 : BRUSH_COUNT);
}

export function composeTrianglePathTile(
  atlasId: PathTileAtlasId,
  size: number,
  mask: number,
  up: boolean,
  variant: 'two-arm-trapeze' | 'three-arm-trapeze' | null = null,
  fillCorners: readonly TrianglePathFillCorner[] = [],
): HTMLCanvasElement {
  const normalizedMask = normalizeTriangleMask(mask, up);
  const resolvedFillCorners = fillCorners.length > 0
    ? Array.from(new Set(fillCorners)).sort().join(',')
    : (variant ? 'right' : 'none');
  const cacheKey = `${atlasId}:${size}:${normalizedMask}:${up ? 'up' : 'down'}:${variant ?? 'base'}:${resolvedFillCorners}`;
  const existing = tileCache.get(cacheKey);
  if (existing) {
    return existing;
  }

  const outerCanvas = createCanvas(size, size);
  const outerContext = outerCanvas.getContext('2d');
  if (!outerContext) {
    return outerCanvas;
  }
  const innerCanvas = createCanvas(size, size);
  const innerContext = innerCanvas.getContext('2d');
  if (!innerContext) {
    return outerCanvas;
  }
  outerContext.imageSmoothingEnabled = false;
  innerContext.imageSmoothingEnabled = false;
  outerContext.clearRect(0, 0, size, size);
  innerContext.clearRect(0, 0, size, size);

  const style = getPathTileArtStyle(atlasId);
  const outerWidth = Math.max(10, size * TRIANGLE_OUTER_WIDTH_RATIO);
  const innerWidth = style.borderColor ? Math.max(6, outerWidth - (size * TRIANGLE_INNER_WIDTH_SHRINK_RATIO)) : outerWidth;

  if (style.borderColor) {
    drawPathLayer(outerContext, size, normalizedMask, up, style.borderColor, outerWidth);
  }
  drawPathLayer(innerContext, size, normalizedMask, up, style.fillColor, innerWidth);
  const cornersToFill = fillCorners.length > 0
    ? fillCorners
    : (variant ? ['right'] as const : []);
  for (const corner of cornersToFill) {
    drawCornerFill(innerContext, size, normalizedMask, up, style.fillColor, corner);
  }

  const composed = composePathTileLayers({
    atlasId,
    tileSize: size,
    outerMask: style.borderColor ? outerCanvas : null,
    innerMask: innerCanvas,
    decorateInner: style.decoration?.kind === 'flowers'
      ? (target) => {
        const targetContext = target.getContext('2d');
        if (!targetContext) {
          return;
        }
        drawFlowerDecoration(
          targetContext,
          size,
          `${atlasId}:${normalizedMask}:${up ? 'up' : 'down'}`,
          up,
          style.decoration.count,
          style.decoration.bloomColors,
          style.decoration.centerColor,
          style.decoration.stemColor,
        );
      }
      : null,
  });

  tileCache.set(cacheKey, composed);
  return composed;
}

export function composeTrianglePathBrushPreview(
  atlasId: PathTileAtlasId,
  tileSize: number,
  tileIndex: number,
  _textureImage?: HTMLImageElement,
): HTMLCanvasElement {
  const brush = resolveTrianglePathBrush(tileIndex);
  if (!brush) {
    return composeTrianglePathTile(atlasId, tileSize, 0, true);
  }
  return composeTrianglePathTile(
    atlasId,
    tileSize,
    brush.kind === 'variant' ? resolveTriangleVariantMask(brush.variant, true) : brush.mask,
    true,
    brush.kind === 'variant' ? brush.variant : null,
  );
}

export function composeTrianglePathPreviewAtlas(atlasId: PathTileAtlasId, tileSize: number): HTMLCanvasElement {
  const cacheKey = `${atlasId}:${tileSize}:preview`;
  const existing = previewAtlasCache.get(cacheKey);
  if (existing) {
    return existing;
  }

  const columns = 4;
  const rows = 4;
  const canvas = createCanvas(columns * tileSize, rows * tileSize);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }
  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, canvas.width, canvas.height);
  for (let index = 0; index < BRUSH_COUNT; index += 1) {
    const source = composeTrianglePathTile(atlasId, tileSize, index, true);
    const drawX = (index % columns) * tileSize;
    const drawY = Math.floor(index / columns) * tileSize;
    context.drawImage(source, drawX, drawY);
  }

  previewAtlasCache.set(cacheKey, canvas);
  return canvas;
}

export function composeTrianglePathAtlas(atlasId: PathTileAtlasId, tileSize: number): HTMLCanvasElement {
  const cacheKey = `${atlasId}:${tileSize}:runtime`;
  const existing = runtimeAtlasCache.get(cacheKey);
  if (existing) {
    return existing;
  }

  const columns = 8;
  const rows = 4;
  const canvas = createCanvas(columns * tileSize, rows * tileSize);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }
  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, canvas.width, canvas.height);
  for (let index = 0; index < RUNTIME_TILE_COUNT; index += 1) {
    const mask = index % BRUSH_COUNT;
    const up = index < BRUSH_COUNT;
    const source = composeTrianglePathTile(atlasId, tileSize, mask, up);
    const drawX = (index % columns) * tileSize;
    const drawY = Math.floor(index / columns) * tileSize;
    context.drawImage(source, drawX, drawY);
  }

  runtimeAtlasCache.set(cacheKey, canvas);
  return canvas;
}
